/* eslint-disable react/prop-types */
import React, { useEffect, useCallback } from 'react';
import { FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import * as turf from '@turf/turf';
import L from 'leaflet';
import { isEmpty } from 'lodash';

const MapComponent = ({
  uploadedGeoJSONLayer,
  isMapEditable = true,
  featureGroupRef,
  hasLayer,
  setHasLayer,
  setPolygonData,
  setAreaHectares,
  setAreaAcres,
  setPerimeter
}) => {
  // Helper function to calculate and update area
  const updateAreaCalculations = useCallback(
    (layer, geoJSON) => {
      try {
        const area = turf.area(geoJSON);
        const areaHectares = area / 10000;
        const areaAcres = area / 4046.8564224;
        const line = turf.lineString(geoJSON.geometry.coordinates[0]);
        const perimeter = turf.length(line, { units: 'meters' });

        // Update the tooltip
        if (layer && layer.bindTooltip) {
          layer
            .bindTooltip(
              `Area: ${areaHectares.toFixed(2)} ha / ${areaAcres.toFixed(
                2
              )} acres`
            )
            .openTooltip();
        }

        // Update state
        setPerimeter(perimeter);
        setAreaHectares(areaHectares);
        setAreaAcres(areaAcres);
        setPolygonData(geoJSON);
      } catch (error) {
        console.error('Error calculating area:', error);
      }
    },
    [setAreaHectares, setAreaAcres, setPolygonData]
  );

  useEffect(() => {
    if (
      uploadedGeoJSONLayer &&
      !isEmpty(uploadedGeoJSONLayer) &&
      featureGroupRef.current
    ) {
      try {
        featureGroupRef.current.clearLayers();

        const leafletLayer = L.geoJSON(uploadedGeoJSONLayer, {
          interactive: true,
          bubblingMouseEvents: true,
          pmIgnore: false
        }).setStyle({
          color: '#ff0000',
          fillColor: '#ff0000',
          fillOpacity: 0.2
        });

        const polygonLayer = Object.values(leafletLayer._layers)[0];
        if (polygonLayer) {
          polygonLayer.options.editing = true;
          featureGroupRef.current.addLayer(polygonLayer);
          setHasLayer(true);

          updateAreaCalculations(polygonLayer, uploadedGeoJSONLayer);
        }
      } catch (error) {
        console.error('Error adding layer:', error);
      }
    }
  }, [
    uploadedGeoJSONLayer,
    featureGroupRef,
    updateAreaCalculations,
    setHasLayer
  ]);

  const _onCreate = e => {
    console.log(e, 'created');
    const { layer } = e;
    if (featureGroupRef.current) {
      // Clear existing layers before adding new one
      featureGroupRef.current.clearLayers();
      featureGroupRef.current.addLayer(layer);

      setHasLayer(true);
      const geoJSON = layer.toGeoJSON();
      if (geoJSON.geometry.type === 'Polygon') {
        updateAreaCalculations(layer, geoJSON);
      }
    }
  };

  const _onEdited = e => {
    try {
      const {
        layers: { _layers }
      } = e;
      // Get the edited layer
      const layer = Object.values(_layers)[0];
      if (layer) {
        const geoJSON = layer.toGeoJSON();
        if (geoJSON.geometry.type === 'Polygon') {
          // Force update the feature group
          if (featureGroupRef.current) {
            featureGroupRef.current.clearLayers();
            featureGroupRef.current.addLayer(layer);
          }
          updateAreaCalculations(layer, geoJSON);
        }
      }
    } catch (error) {
      console.error('Error in edit handler:', error);
    }
  };

  const _onDeleted = () => {
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    setHasLayer(false);
    setPolygonData(null);
    setAreaHectares(0);
    setAreaAcres(0);
    setPerimeter(0);
  };

  return (
    <FeatureGroup ref={featureGroupRef}>
      {isMapEditable && (
        <EditControl
          position="bottomright"
          onCreated={_onCreate}
          onEdited={_onEdited}
          onDeleted={_onDeleted}
          draw={{
            circlemarker: false,
            marker: false,
            polyline: false,
            rectangle: false,
            circle: false,
            polygon: hasLayer
              ? false
              : {
                  showArea: true,
                  allowIntersection: false,
                  drawError: {
                    color: '#e1e100',
                    message: "<strong>Oh snap!<strong> you can't draw that!"
                  },
                  shapeOptions: {
                    color: '#ff0000',
                    fillColor: '#ff0000',
                    fillOpacity: 0.2
                  }
                }
          }}
          edit={{
            featureGroup: featureGroupRef.current,
            remove: isMapEditable,
            edit: isMapEditable,
            editMode: {
              selectedPathOptions: {
                maintainColor: true,
                moveMarkers: true,
                color: '#ff0000'
              }
            }
          }}
        />
      )}
    </FeatureGroup>
  );
};

export default MapComponent;
